import agent, { ListData } from "../api/agent";
import { User } from "../models/user";
import { router } from "../router/Routes";
import { UserRegisterDto } from "../models/userRegisterDto";
import { UserRole } from "../common/enums/userRole";
import { DataTableList } from "../common/interfaces/TableData";
import { ChangePassword } from "../common/interfaces/ChangePassword";
import { FilterProps } from "../common/DataTable";
import { useStore } from "./store";

export type UserStore = {
    tableData: DataTableList<User>;
    selectedUser?: User;
    loggedUser: User | null;
    loadingInitial: boolean;
    editMode: boolean;
    loading: boolean;
    isLoggedIn: boolean;
    userRole?: UserRole;

    login: (creds: UserRegisterDto) => Promise<void>;
    register: (creds: UserRegisterDto) => Promise<void>;
    changePassword: (data: ChangePassword) => Promise<any>;
    logout: () => void;
    getUser: () => Promise<void>;
    deleteUser: (id: string) => Promise<void>;
    loadUsers: (filterProps: FilterProps) => Promise<ListData<User> | undefined>;
    setLoadingInitial: (state: boolean) => void;
};

export const createUserStore = (set: any, get: any): UserStore => ({
    tableData: new DataTableList<User>(),
    selectedUser: undefined,
    loggedUser: null,
    loadingInitial: false,
    editMode: false,
    loading: false,
    isLoggedIn: false,
    userRole: undefined,

    login: async (creds: UserRegisterDto) => {
        const user = await agent.Users.login(creds);
        get().commonStore.setToken(user.token);
        set((state: any) => ({
            userStore: {
                ...state.userStore,
                loggedUser: user,
                isLoggedIn: true,
                userRole: user?.roles?.[0],
            },
        }));
        router.navigate("/home");
        get().modalStore.closeModal();
    },

    register: async (creds: UserRegisterDto) => {
        await agent.Users.register(creds);
        await get().userStore.loadUsers({} as any);
    },

    changePassword: async (data: ChangePassword) => {
        return agent.Users.changePassword(data);
    },

    logout: () => {
        get().commonStore.setToken(null);
        set((state: any) => ({
            userStore: {
                ...state.userStore,
                loggedUser: null,
                isLoggedIn: false,
                userRole: undefined,
            },
        }));
        router.navigate("/");
    },

    getUser: async () => {
        try {
            const user = await agent.Users.current();
            set((state: any) => ({
                userStore: {
                    ...state.userStore,
                    loggedUser: user,
                    isLoggedIn: !!user,
                    userRole: user?.roles?.[0],
                },
            }));
        } catch (error) {
            console.log(error);
        }
    },

    deleteUser: async (id: string) => {
        set((state: any) => ({
            userStore: { ...state.userStore, loading: true },
        }));
        try {
            await agent.Users.delete(id);
            set((state: any) => {
                const map = new Map(state.userStore.tableData.list);
                map.delete(id);
                return {
                    userStore: {
                        ...state.userStore,
                        tableData: { ...state.userStore.tableData, list: map },
                        loading: false,
                    },
                };
            });
        } catch (error) {
            console.log(error);
            set((state: any) => ({
                userStore: { ...state.userStore, loading: false },
            }));
            return Promise.reject(error);
        }
    },

    loadUsers: async (filterProps: FilterProps) => {
        set((state: any) => ({
            userStore: { ...state.userStore, loadingInitial: true },
        }));
        try {
            const result = await agent.Users.list(filterProps);
            const list = result.data.reduce((acc: Map<string, User>, item: User) => {
                acc.set(item.id!, item);
                return acc;
            }, new Map<string, User>());
            set((state: any) => ({
                userStore: {
                    ...state.userStore,
                    tableData: {
                        list,
                        currentPage: result.currentPage,
                        pageSize: result.pageSize,
                        totalRecords: result.totalRecords,
                        orderBy: result.orderBy,
                    },
                    loadingInitial: false,
                },
            }));
            return result;
        } catch (error) {
            console.log(error);
            set((state: any) => ({
                userStore: { ...state.userStore, loadingInitial: false },
            }));
        }
    },

    setLoadingInitial: (stateValue: boolean) => {
        set((state: any) => ({
            userStore: { ...state.userStore, loadingInitial: stateValue },
        }));
    },
});

export const useUserStore = () => useStore((s) => s.userStore);
